/**
 * Floating edge geometry (React Flow v12).
 *
 * Instead of pinning an edge to a fixed handle, we attach it to the point on
 * each node's boundary that faces the other node. This makes the arrowhead
 * enter the node *perpendicular to the boundary it touches*, so it always
 * points cleanly INTO the node — no more arrows bent sideways while the line
 * arrives from the top.
 *
 * Standard React Flow "floating edges" boundary-intersection algorithm,
 * adapted to v12 (`node.internals.positionAbsolute` + `node.measured`).
 */
import { Position, type InternalNode, type Node } from '@xyflow/react'

type XY = { x: number; y: number }

/**
 * The node's visual rotation in degrees (clockwise, screen y-down) as written by
 * RotateHandle into `data.rotation`. The node CONTENT is rotated via CSS
 * `transform: rotate(Ndeg)` about its center, while its layout bounding box
 * (positionAbsolute + measured) stays axis-aligned. To make connectors meet the
 * rotated boundary we transform geometry into the node's local (un-rotated)
 * frame, solve there, then rotate the result back into world space.
 *
 * Returns 0 for missing / non-finite values so the un-rotated code path is taken.
 */
export function getNodeRotation(node: InternalNode<Node>): number {
  const r = (node.data as { rotation?: unknown } | undefined)?.rotation
  return typeof r === 'number' && Number.isFinite(r) ? r : 0
}

/** Center of a node's axis-aligned bounding box in world (flow) coordinates. */
function getNodeCenter(node: InternalNode<Node>): XY {
  return {
    x: node.internals.positionAbsolute.x + (node.measured?.width ?? 0) / 2,
    y: node.internals.positionAbsolute.y + (node.measured?.height ?? 0) / 2,
  }
}

/** Rotate `p` by `deg` (clockwise, y-down) around `center`. */
function rotateAround(p: XY, center: XY, deg: number): XY {
  if (!deg) return { x: p.x, y: p.y }
  const rad = (deg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const dx = p.x - center.x
  const dy = p.y - center.y
  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  }
}

/**
 * Intersection of the line between two node centers with `intersectionNode`'s
 * rectangle, honoring the node's visual rotation.
 *
 * Rotation 0 (the common case) takes the original code path UNCHANGED so output
 * is byte-identical. When rotated, we map the other node's center into the
 * rotated node's local frame (rotate by -rotation), solve the axis-aligned
 * intersection there, then rotate the hit point back into world space.
 */
function getNodeIntersection(
  intersectionNode: InternalNode<Node>,
  targetNode: InternalNode<Node>,
): XY {
  const w = (intersectionNode.measured?.width ?? 0) / 2
  const h = (intersectionNode.measured?.height ?? 0) / 2
  const i = intersectionNode.internals.positionAbsolute

  const x2 = i.x + w
  const y2 = i.y + h

  const rotation = getNodeRotation(intersectionNode)
  // Other node center in world space.
  const tc = getNodeCenter(targetNode)
  // Map it into the intersection node's local (un-rotated) frame when needed.
  const local = rotation ? rotateAround(tc, { x: x2, y: y2 }, -rotation) : tc
  const x1 = local.x
  const y1 = local.y

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h)
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h)
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1) || 1)
  const xx3 = a * xx1
  const yy3 = a * yy1

  const localHit = { x: w * (xx3 + yy3) + x2, y: h * (-xx3 + yy3) + y2 }
  // Rotate the local hit point back into world space (no-op when rotation is 0).
  return rotation ? rotateAround(localHit, { x: x2, y: y2 }, rotation) : localHit
}

/**
 * Which side (Top/Right/Bottom/Left) of `node` the intersection `point` sits on.
 * This is the node's OWN side (used as the path tangent / handle position), so
 * for a rotated node we classify against the local (un-rotated) frame: a hit on
 * the node's right side stays Position.Right even when it visually faces down.
 *
 * Rotation 0 takes the original code path UNCHANGED (byte-identical output).
 */
function getEdgePosition(node: InternalNode<Node>, point: XY): Position {
  const pos = node.internals.positionAbsolute
  const w = node.measured?.width ?? 0
  const h = node.measured?.height ?? 0
  const rotation = getNodeRotation(node)

  // For rotated nodes, transform the world point back into the local frame so
  // the side test compares against the axis-aligned rectangle.
  const local = rotation
    ? rotateAround(point, getNodeCenter(node), -rotation)
    : point

  const nx = Math.round(pos.x)
  const ny = Math.round(pos.y)
  const px = Math.round(local.x)
  const py = Math.round(local.y)

  if (px <= nx + 1) return Position.Left
  if (px >= nx + w - 1) return Position.Right
  if (py <= ny + 1) return Position.Top
  if (py >= ny + h - 1) return Position.Bottom
  return Position.Top
}

export interface FloatingEdgeParams {
  sx: number
  sy: number
  tx: number
  ty: number
  sourcePos: Position
  targetPos: Position
}

/**
 * A point on a node's rectangular boundary at a given angle around its center.
 * Angle convention (screen coords, y-down): 0° = right, 90° = down, 180° = left,
 * 270° = up. Lets the user "spin" an edge's attachment point around the node.
 */
export function getPerimeterPoint(
  node: InternalNode<Node>,
  angleDeg: number,
): XY & { pos: Position } {
  const w = (node.measured?.width ?? 0) / 2
  const h = (node.measured?.height ?? 0) / 2
  const cx = node.internals.positionAbsolute.x + w
  const cy = node.internals.positionAbsolute.y + h
  const rad = (angleDeg * Math.PI) / 180
  const dx = Math.cos(rad)
  const dy = Math.sin(rad)
  // Scale the unit ray so it lands exactly on the rectangle's edge.
  const scale = 1 / Math.max(Math.abs(dx) / (w || 1), Math.abs(dy) / (h || 1))
  // Local (un-rotated) attachment point. `angleDeg` is measured around the
  // node's own axes, so the side classification below stays in the local frame.
  const x = cx + dx * scale
  const y = cy + dy * scale

  let pos: Position
  if (x <= cx - w + 1) pos = Position.Left
  else if (x >= cx + w - 1) pos = Position.Right
  else if (y <= cy - h + 1) pos = Position.Top
  else pos = Position.Bottom

  // Apply the node's visual rotation so the point meets the rotated boundary.
  // No-op when rotation is 0 → byte-identical to the previous behavior.
  const rotation = getNodeRotation(node)
  const world = rotation ? rotateAround({ x, y }, { x: cx, y: cy }, rotation) : { x, y }

  return { x: world.x, y: world.y, pos }
}

/** Boundary-to-boundary endpoints + the side each one touches. */
export function getFloatingEdgeParams(
  source: InternalNode<Node>,
  target: InternalNode<Node>,
): FloatingEdgeParams {
  const sp = getNodeIntersection(source, target)
  const tp = getNodeIntersection(target, source)
  return {
    sx: sp.x,
    sy: sp.y,
    tx: tp.x,
    ty: tp.y,
    sourcePos: getEdgePosition(source, sp),
    targetPos: getEdgePosition(target, tp),
  }
}

// ============================================================================
// SEAM-6 — fixed-connection (12-handle) positions follow node rotation
// ============================================================================

/** Unit center offset for each side: which face the handle sits on. */
const SIDE_UNIT: Record<string, { ux: number; uy: number }> = {
  top: { ux: 0, uy: -1 },
  bottom: { ux: 0, uy: 1 },
  left: { ux: -1, uy: 0 },
  right: { ux: 1, uy: 0 },
}
/**
 * Slot shift along the side's free axis, as a fraction of the HALF extent.
 * Mirrors IconLibraryNode's 12-handle DOM placement: the left/top slots sit at
 * 25% (−0.5 of the half extent from center) and right/bottom at 75% (+0.5).
 */
const SLOT_SHIFT: Record<string, number> = {
  left: -0.5,
  top: -0.5,
  center: 0,
  right: 0.5,
  bottom: 0.5,
}

/**
 * Map a 12-handle id (e.g. `"top-center-source"`, `"right-bottom-target"`) to a
 * center-relative unit offset in [-1, 1] on each axis. Returns `null` for ids
 * outside this convention (ShapeNode's `t`/`tr`/`angle-N`/`side-N` handles), so
 * callers can leave such endpoints untouched.
 *
 * The first token is the side, the second the slot. For top/bottom sides the
 * slot shifts along x; for left/right it shifts along y. Slot shifts are ±0.5 so
 * the offset lands exactly on the rendered 25%/75% handle position.
 */
export function handleUnitOffset(
  handleId: string,
): { ux: number; uy: number } | null {
  const [side, slot] = handleId.split('-')
  const base = SIDE_UNIT[side]
  if (!base) return null
  const shift = SLOT_SHIFT[slot] ?? 0
  if (side === 'top' || side === 'bottom') return { ux: shift, uy: base.uy }
  return { ux: base.ux, uy: shift }
}

/**
 * World position of a fixed 12-handle after the node is rotated `rotationDeg`
 * (clockwise, screen y-down) about its center. Computes the handle's local
 * offset from center, then rotates it into world space. Returns `null` when the
 * handle id is not part of the 12-handle convention.
 */
export function rotateHandleOffset(
  cx: number,
  cy: number,
  halfW: number,
  halfH: number,
  handleId: string,
  rotationDeg: number,
): { x: number; y: number } | null {
  const u = handleUnitOffset(handleId)
  if (!u) return null
  const lx = u.ux * halfW
  const ly = u.uy * halfH
  const rad = (rotationDeg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return { x: cx + lx * cos - ly * sin, y: cy + lx * sin + ly * cos }
}
